import { AlertTriangle, ArrowLeft, RefreshCw, WifiOff } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useRouteError } from 'react-router-dom';
import { Button, buttonVariants } from '@/components/ui';

/** Recovery stays in the app; no cache clearing, logout, or automatic reload of pending work. */
export function RouteErrorPage() {
  const { t } = useTranslation();
  const error = useRouteError();
  const [online, setOnline] = useState(navigator.onLine);
  useEffect(() => {
    const update = () => setOnline(navigator.onLine);
    window.addEventListener('online', update);
    window.addEventListener('offline', update);
    return () => {
      window.removeEventListener('online', update);
      window.removeEventListener('offline', update);
    };
  }, []);
  const loadFailure = error instanceof Error && /dynamically imported module|loading chunk|chunkloaderror|importing a module script/i.test(`${error.name} ${error.message}`);
  const Icon = online ? AlertTriangle : WifiOff;
  return (
    <section className="mx-auto my-8 max-w-lg px-4" role="alert" aria-labelledby="route-error-title">
      <div className="overflow-hidden rounded-2xl border border-stone-200 bg-white shadow-sm">
        <div className="h-1.5 bg-brand-800" />
        <div className="space-y-5 p-6 sm:p-8">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-800">
            <Icon className="h-7 w-7" aria-hidden="true" />
          </div>
          <div className="space-y-2">
            <p className="text-sm font-semibold text-brand-800">{t('app.name')}</p>
            <h1 id="route-error-title" className="text-2xl font-bold tracking-tight text-stone-950">
              {t(!online && loadFailure ? 'routeError.offlineTitle' : 'routeError.title')}
            </h1>
            <p className="leading-relaxed text-stone-600">
              {t(!online && loadFailure ? 'routeError.offlineBody' : loadFailure ? 'routeError.loadBody' : 'routeError.body')}
            </p>
          </div>
          <p className="rounded-xl bg-stone-50 p-4 text-sm leading-relaxed text-stone-700">{t('routeError.savedData')}</p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link to="/" className={buttonVariants({ variant: 'secondary' })}>
              <ArrowLeft className="h-4 w-4" aria-hidden="true" /> {t('routeError.home')}
            </Link>
            <Button disabled={!online} onClick={() => window.location.reload()}>
              <RefreshCw className="h-4 w-4" aria-hidden="true" /> {t('app.reload')}
            </Button>
          </div>
          <p className="text-sm text-stone-500">{t(online ? 'routeError.reloadHint' : 'routeError.reconnectHint')}</p>
        </div>
      </div>
    </section>
  );
}
